import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  PropsWithChildren,
  useContext,
  useId,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode
} from 'react';
import { cn } from 'utils/cn';

export type TabVariant = 'segmented' | 'bar' | 'card' | 'underline';

interface TabVariantClasses {
  tabList: string;
  tab: string;
  tabInactive: string;
  tabActive: string;
}

export interface TabsContextValue {
  selectedIndex: number;
  onSelect: (index: number) => void;
  variant: TabVariant;
  selectedTabClassName?: string;
  baseId: string;
}

const FOCUS_RING =
  'focus:outline-none focus:ring-2 focus:ring-primary-500/50 dark:focus:ring-primary-dark-500/50';

const TAB_TEXT_INACTIVE =
  'text-gray-600 dark:text-secondary-dark-900 hover:text-gray-800 dark:hover:text-primary-dark-700';

const TAB_TEXT_ACTIVE = 'text-gray-900 dark:text-primary-dark-600';

export const TAB_DISPLAY_NAME = 'UIKitTab';
export const TAB_PANEL_DISPLAY_NAME = 'UIKitTabPanel';

export const TAB_VARIANT_CLASSES: Record<TabVariant, TabVariantClasses> = {
  segmented: {
    tabList:
      'inline-flex items-center ~p-0.5/1 ~gap-0.5/1 rounded-lg bg-secondary-200 dark:bg-secondary-dark-200 shadow-sm',
    tab: `min-w-max ~px-3/4 ~py-1/1.5 ~text-xs/sm rounded-md font-medium transition-all duration-200 ease-in-out cursor-pointer ${FOCUS_RING}`,
    tabInactive: TAB_TEXT_INACTIVE,
    tabActive: `bg-white dark:bg-primary-dark-200 ${TAB_TEXT_ACTIVE} shadow-sm font-medium`
  },
  bar: {
    tabList:
      'w-full h-fit flex items-center sm:items-start ~gap-2/4 overflow-x-auto scrollbar-thin sticky top-0 z-50 bg-secondary-50 dark:bg-secondary-dark-200 ~p-1.5/2 rounded-t-md',
    tab: `min-w-max ~text-xs/base ~px-2/4 ~py-1.5/2.5 cursor-pointer border-secondary-300 dark:border-secondary-dark-400 flex items-center gap-2 ${FOCUS_RING}`,
    tabInactive: TAB_TEXT_INACTIVE,
    tabActive: `dark:bg-secondary-dark-300 bg-secondary-100 border-t border-secondary-300 dark:border-secondary-dark-400 rounded-t-md ${TAB_TEXT_ACTIVE} font-medium`
  },
  card: {
    tabList:
      "w-full h-auto max-h-full flex items-center sm:items-start ~gap-3/6 sticky top-0 z-50 ~p-1.5/2 overflow-x-auto backdrop-blur-md min-w-[theme('screens.2xs')]",
    tab: `min-w-max ~text-xs/base ~px-2/4 ~py-1.5/2 cursor-pointer rounded-lg flex items-center ~gap-1/2 transition-all duration-75 ${FOCUS_RING}`,
    tabInactive: `${TAB_TEXT_INACTIVE} bg-primary-100 dark:bg-primary-dark-100 hover:bg-primary-200 dark:hover:bg-primary-dark-200`,
    tabActive: `border-2 border-primary-500 dark:border-primary-dark-500 rounded-lg shadow-sm bg-secondary-50 dark:bg-primary-dark-200 ${TAB_TEXT_ACTIVE} font-medium`
  },
  underline: {
    tabList:
      'text-sm font-medium my-2 border-b border-gray-200 dark:border-secondary-dark-500 flex flex-wrap',
    tab: `~px-2/4 ~py-1.5/2 cursor-pointer ${FOCUS_RING}`,
    tabInactive: TAB_TEXT_INACTIVE,
    tabActive: `dark:bg-secondary-dark-300 bg-white border-t border-gray-200 dark:border-secondary-dark-500 rounded-t ${TAB_TEXT_ACTIVE} font-medium`
  }
};

export const TabsContext = createContext<TabsContextValue | null>(null);

export function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs provider');
  }
  return context;
}

export function isTabElement(
  child: ReactNode
): child is ReactElement<Record<string, unknown>> {
  return (
    isValidElement(child) &&
    (child.type as { displayName?: string }).displayName === TAB_DISPLAY_NAME
  );
}

export function isTabPanelElement(
  child: ReactNode
): child is ReactElement<Record<string, unknown>> {
  return (
    isValidElement(child) &&
    (child.type as { displayName?: string }).displayName ===
      TAB_PANEL_DISPLAY_NAME
  );
}

export function assignPanelIndices(children: ReactNode): ReactNode {
  let panelIndex = 0;

  return Children.map(children, (child) => {
    if (!isTabPanelElement(child)) {
      return child;
    }

    const index = panelIndex;
    panelIndex += 1;
    return cloneElement(child, { index });
  });
}

export interface TabsProps extends Omit<
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
  'onSelect'
> {
  selectedIndex?: number;
  defaultIndex?: number;
  onSelect?: (index: number) => void;
  variant?: TabVariant;
  selectedTabClassName?: string;
}

function Tabs({
  children,
  selectedIndex,
  defaultIndex = 0,
  onSelect,
  variant = 'bar',
  selectedTabClassName,
  className,
  ...props
}: TabsProps) {
  const baseId = useId();
  const [internalIndex, setInternalIndex] = useState(defaultIndex);
  const isControlled = selectedIndex !== undefined;
  const currentIndex = isControlled ? selectedIndex : internalIndex;

  const handleSelect = (index: number) => {
    if (!isControlled) {
      setInternalIndex(index);
    }
    onSelect?.(index);
  };

  const contextValue = {
    selectedIndex: currentIndex,
    onSelect: handleSelect,
    variant,
    selectedTabClassName,
    baseId
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={cn(className)} {...props}>
        {assignPanelIndices(children)}
      </div>
    </TabsContext.Provider>
  );
}

export { Tab } from './Tab';
export type { TabProps } from './Tab';
export { default as TabList } from './TabList';
export { TabPanel } from './TabPanel';
export type { TabPanelProps } from './TabPanel';

export default Tabs;
